import {
  Anchor,
  Badge,
  Breadcrumbs,
  Button,
  Card,
  Container,
  Divider,
  Grid,
  Group,
  Modal,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LuDownload } from 'react-icons/lu'
import { CurrencyText } from '@/frontend/components/marina/CurrencyText'
import { MoneyText } from '@/frontend/components/marina/MoneyText'
import { StatusBadge } from '@/frontend/components/marina/StatusBadge'
import {
  useAcceptQuote,
  useConvertQuoteToInvoice,
  useQuote,
  useRejectQuote,
  useSendQuote,
} from '@/frontend/hooks/useQuotes'
import { humanizeError } from '@/frontend/lib/errors'

export const Route = createFileRoute('/dashboard/billing/quotes/$id')({
  component: QuoteDetail,
})

function QuoteDetail() {
  const { id } = Route.useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { data, isLoading } = useQuote(id)
  const send = useSendQuote()
  const accept = useAcceptQuote()
  const reject = useRejectQuote()
  const convert = useConvertQuoteToInvoice()

  const [convertOpened, convertH] = useDisclosure(false)

  if (isLoading)
    return (
      <Container size="xl" py="md">
        <Text c="dimmed">{t('page.billing.quotes.detail.loading')}</Text>
      </Container>
    )
  if (!data)
    return (
      <Container size="xl" py="md">
        <Text c="dimmed">{t('page.billing.quotes.detail.not_found')}</Text>
      </Container>
    )
  const q = data.quote
  const customerName = q.customer ? `${q.customer.firstName} ${q.customer.lastName}`.trim() : '—'

  const notify = async (p: Promise<unknown>, success: string) => {
    try {
      await p
      notifications.show({ title: t('common.updated'), message: success, color: 'teal' })
    } catch (e: unknown) {
      notifications.show({ title: t('common.failed'), message: humanizeError(e, t), color: 'red' })
    }
  }

  const confirm = (
    title: string,
    body: string,
    confirmLabel: string,
    fn: () => Promise<void>,
    color: 'teal' | 'red' = 'teal',
  ) =>
    modals.openConfirmModal({
      title,
      children: <Text size="sm">{body}</Text>,
      labels: { confirm: confirmLabel, cancel: t('common.cancel', { defaultValue: 'Cancel' }) },
      confirmProps: { color },
      onConfirm: fn,
    })

  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        <Breadcrumbs>
          <Anchor component={Link} to="/dashboard/billing/quotes" c="dimmed">
            {t('page.billing.quotes.title')}
          </Anchor>
          <span>{q.code}</span>
        </Breadcrumbs>

        <Card padding="lg" radius="md">
          <Group justify="space-between" align="flex-start">
            <div>
              <Group gap="xs" mb={4}>
                <Title order={3}>{q.code}</Title>
                <StatusBadge status={q.status} kind="quote" />
              </Group>
              <Text c="dimmed" size="sm">
                {customerName}
                {q.boat?.name && <> · {q.boat.name}</>}
              </Text>
              <Text c="dimmed" size="sm">
                Issued {dayjs(q.issueDate).format('DD MMM YYYY')}
                {q.validUntil && <> · Valid until {dayjs(q.validUntil).format('DD MMM YYYY')}</>}
              </Text>
            </div>
            <Group gap="xs">
              <Button
                variant="default"
                leftSection={<LuDownload size={16} />}
                component="a"
                href={`/api/quotes/${q.id}/pdf`}
                target="_blank"
              >
                PDF
              </Button>
              {q.status === 'DRAFT' && (
                <Button
                  color="laut"
                  onClick={() =>
                    confirm(
                      t('page.billing.quotes.detail.action_send_title'),
                      t('page.billing.quotes.detail.action_send_body'),
                      t('page.billing.quotes.detail.action_send'),
                      () => notify(send.mutateAsync(q.id), t('page.billing.quotes.detail.action_send')),
                    )
                  }
                >
                  {t('page.billing.quotes.detail.action_send')}
                </Button>
              )}
              {q.status === 'SENT' && (
                <>
                  <Button
                    color="laut"
                    onClick={() =>
                      confirm(
                        t('page.billing.quotes.detail.action_accept_title'),
                        t('page.billing.quotes.detail.action_accept_body'),
                        t('page.billing.quotes.detail.action_accept'),
                        () => notify(accept.mutateAsync(q.id), t('page.billing.quotes.detail.action_accept')),
                      )
                    }
                  >
                    {t('page.billing.quotes.detail.action_accept')}
                  </Button>
                  <Button
                    variant="light"
                    color="red"
                    onClick={() =>
                      confirm(
                        t('page.billing.quotes.detail.action_reject_title'),
                        t('page.billing.quotes.detail.action_reject_body'),
                        t('page.billing.quotes.detail.action_reject'),
                        () => notify(reject.mutateAsync(q.id), t('page.billing.quotes.detail.action_reject')),
                        'red',
                      )
                    }
                  >
                    {t('page.billing.quotes.detail.action_reject')}
                  </Button>
                </>
              )}
              {(q.status === 'ACCEPTED' || q.status === 'SENT') && !q.invoice && (
                <Button color="karang" onClick={convertH.open}>
                  {t('page.billing.quotes.detail.action_convert')}
                </Button>
              )}
              {q.invoice && (
                <Badge
                  variant="light"
                  component="a"
                  href={`/dashboard/billing/invoices/${q.invoice.id}`}
                  style={{ cursor: 'pointer' }}
                >
                  → {q.invoice.code}
                </Badge>
              )}
            </Group>
          </Group>
        </Card>

        <Grid>
          <Grid.Col span={{ base: 12, md: 8 }}>
            <Card padding="lg" radius="md">
              <Text fw={600} mb="sm">
                Line Items
              </Text>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Description</Table.Th>
                    <Table.Th ta="right">Qty</Table.Th>
                    <Table.Th ta="right">Unit Price</Table.Th>
                    <Table.Th ta="right">Amount</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {(q.lineItems ?? []).map((it) => (
                    <Table.Tr key={it.id}>
                      <Table.Td>{it.description}</Table.Td>
                      <Table.Td ta="right">{Number(it.quantity)}</Table.Td>
                      <Table.Td ta="right">
                        <CurrencyText idr={it.unitPriceIdr} />
                      </Table.Td>
                      <Table.Td ta="right">
                        <CurrencyText idr={it.amountIdr} fw={500} />
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
              <Divider my="md" />
              <Stack gap={4} align="flex-end">
                <Group gap="md">
                  <Text size="sm" c="dimmed">
                    Subtotal
                  </Text>
                  <CurrencyText idr={q.subtotalIdr} miw={140} ta="right" />
                </Group>
                <Group gap="md">
                  <Text size="sm" c="dimmed">
                    Tax ({Number(q.taxRate)}%)
                  </Text>
                  <CurrencyText idr={q.taxIdr} miw={140} ta="right" />
                </Group>
                <Group gap="md">
                  <Text fw={700} size="sm">
                    Total
                  </Text>
                  <MoneyText
                    amount={q.totalIdr}
                    currency={q.currency}
                    fw={700}
                    size="lg"
                    c="laut.7"
                    miw={140}
                    ta="right"
                  />
                </Group>
              </Stack>
            </Card>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 4 }}>
            <Card padding="lg" radius="md">
              <Text fw={600} mb="sm">
                Customer
              </Text>
              <Stack gap={4}>
                <Info label="Name" value={customerName} />
                <Info label="Email" value={q.customer?.email} />
              </Stack>
            </Card>
            {q.notes && (
              <Card padding="lg" radius="md" mt="md">
                <Text fw={600} mb="sm">
                  Notes
                </Text>
                <Text size="sm">{q.notes}</Text>
              </Card>
            )}
          </Grid.Col>
        </Grid>
      </Stack>

      <ConvertModal
        opened={convertOpened}
        onClose={convertH.close}
        onConfirm={async (dueDate) => {
          try {
            const { invoice } = await convert.mutateAsync({ id: q.id, dueDate })
            notifications.show({
              title: 'Converted',
              message: `Invoice ${invoice.code} created.`,
              color: 'teal',
            })
            convertH.close()
            navigate({ to: `/dashboard/billing/invoices/${invoice.id}` as any })
          } catch (e: unknown) {
            notifications.show({
              title: 'Failed',
              message: e instanceof Error ? e.message : 'Try again',
              color: 'red',
            })
          }
        }}
      />
    </Container>
  )
}

function ConvertModal({
  opened,
  onClose,
  onConfirm,
}: {
  opened: boolean
  onClose: () => void
  onConfirm: (dueDate: string) => void
}) {
  const { t } = useTranslation()
  const [dueDate, setDueDate] = useState(dayjs().add(14, 'day').format('YYYY-MM-DD'))
  return (
    <Modal opened={opened} onClose={onClose} title={t('page.billing.quotes.detail.convert_modal_title')}>
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          {t('page.billing.quotes.detail.convert_modal_body')}
        </Text>
        <TextInput
          label={t('page.billing.quotes.detail.convert_modal_due')}
          type="date"
          required
          value={dueDate}
          onChange={(e) => setDueDate(e.currentTarget.value)}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button color="laut" onClick={() => onConfirm(dueDate)}>
            {t('page.billing.quotes.detail.convert_modal_submit')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}

function Info({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Group justify="space-between">
      <Text size="sm" c="dimmed">
        {label}
      </Text>
      <Text size="sm">{value || '—'}</Text>
    </Group>
  )
}
